/**
 * Section Registry and Tier Configurations
 * Defines all available sections, their metadata, and default layouts per tier
 */

/**
 * Section metadata - defines properties of each section component
 */
export const SECTION_METADATA = {
  hero: {
    id: 'hero',
    name: 'Hero',
    component: 'HeroSection',
    description: 'Hero banner with prospect name and status',
    alwaysOn: true,
    icon: 'star',
  },
  'progress-bar': {
    id: 'progress-bar',
    name: 'Progress',
    component: 'ProgressBar',
    description: 'Dynamic progress indicator based on status',
    alwaysOn: true,
    icon: 'progress',
  },
  'why-talk-australia': {
    id: 'why-talk-australia',
    name: 'Why Talk Australia',
    component: 'WhyTalkAustralia',
    description: 'Trust signals: 5-Star Reviews, Family Owned, Local, One-Stop Shop, We Understand Your Business',
    alwaysOn: true,
    icon: 'heart',
  },
  'pain-points': {
    id: 'pain-points',
    name: 'Pain Points',
    component: 'PainPointsSection',
    description: 'Industry-specific pain points and challenges',
    alwaysOn: false,
    defaultTiers: ['enterprise'],
    icon: 'alert',
  },
  'proposal-pricing': {
    id: 'proposal-pricing',
    name: 'Proposal & Pricing',
    component: 'ProposalCard',
    description: 'Tier-aware pricing: single plan (SMB), options (midmarket), workstreams (enterprise)',
    alwaysOn: true,
    icon: 'dollar',
  },
  'department-map': {
    id: 'department-map',
    name: 'Department Map',
    component: 'DepartmentMap',
    description: 'Interactive department tabs showing organizational benefits',
    alwaysOn: false,
    defaultTiers: ['enterprise'],
    icon: 'building',
  },
  'journey-roadmap': {
    id: 'journey-roadmap',
    name: 'Journey Roadmap',
    component: 'JourneyRoadmap',
    description: 'Implementation timeline and milestones',
    alwaysOn: false,
    defaultTiers: ['midmarket', 'enterprise'],
    icon: 'map',
  },
  'portal-features': {
    id: 'portal-features',
    name: 'Portal Features',
    component: 'PortalFeaturesSection',
    description: 'Key features and capabilities breakdown',
    alwaysOn: false,
    defaultTiers: ['midmarket', 'enterprise'],
    icon: 'grid',
  },
  'use-cases': {
    id: 'use-cases',
    name: 'Use Cases',
    component: 'UseCasesSection',
    description: 'Real-world use cases and success stories',
    alwaysOn: false,
    defaultTiers: ['enterprise'],
    icon: 'book',
  },
  'also-offer': {
    id: 'also-offer',
    name: 'We Also Offer',
    component: 'AlsoOfferSection',
    description: 'Additional services and offerings',
    alwaysOn: false,
    defaultTiers: ['smb', 'midmarket', 'enterprise'],
    icon: 'gift',
  },
  'app-download': {
    id: 'app-download',
    name: 'App Download',
    component: 'AppDownloadSection',
    description: 'Mobile app download CTAs',
    alwaysOn: false,
    defaultTiers: ['midmarket', 'enterprise'],
    icon: 'download',
  },
  chat: {
    id: 'chat',
    name: 'Chat',
    component: 'ChatWidget',
    description: 'Real-time messaging with support team',
    alwaysOn: false,
    defaultTiers: ['enterprise'],
    icon: 'message',
  },
  'accept-cta': {
    id: 'accept-cta',
    name: 'Accept CTA',
    component: 'AcceptCTA',
    description: 'Call-to-action with e-sign modal',
    alwaysOn: true,
    icon: 'check',
  },
  footer: {
    id: 'footer',
    name: 'Footer',
    component: 'Footer',
    description: 'Page footer with contact info and links',
    alwaysOn: true,
    icon: 'footer',
  },
};

/**
 * Default section configurations by tier
 * This defines which sections appear by default for each prospect tier
 */
export const DEFAULT_TIER_CONFIGS = {
  smb: {
    tier: 'smb',
    sections: [
      { id: 'hero', visible: true, order: 1 },
      { id: 'progress-bar', visible: true, order: 2 },
      { id: 'why-talk-australia', visible: true, order: 3 },
      { id: 'proposal-pricing', visible: true, order: 4 },
      { id: 'also-offer', visible: true, order: 5 },
      { id: 'accept-cta', visible: true, order: 6 },
      { id: 'footer', visible: true, order: 7 },
    ],
  },
  midmarket: {
    tier: 'midmarket',
    sections: [
      { id: 'hero', visible: true, order: 1 },
      { id: 'progress-bar', visible: true, order: 2 },
      { id: 'why-talk-australia', visible: true, order: 3 },
      { id: 'pain-points', visible: false, order: 4 },
      { id: 'proposal-pricing', visible: true, order: 5 },
      { id: 'journey-roadmap', visible: true, order: 6 },
      { id: 'portal-features', visible: true, order: 7 },
      { id: 'also-offer', visible: true, order: 8 },
      { id: 'app-download', visible: true, order: 9 },
      { id: 'accept-cta', visible: true, order: 10 },
      { id: 'footer', visible: true, order: 11 },
    ],
  },
  enterprise: {
    tier: 'enterprise',
    sections: [
      { id: 'hero', visible: true, order: 1 },
      { id: 'progress-bar', visible: true, order: 2 },
      { id: 'why-talk-australia', visible: true, order: 3 },
      { id: 'pain-points', visible: true, order: 4 },
      { id: 'proposal-pricing', visible: true, order: 5 },
      { id: 'department-map', visible: true, order: 6 },
      { id: 'journey-roadmap', visible: true, order: 7 },
      { id: 'portal-features', visible: true, order: 8 },
      { id: 'use-cases', visible: true, order: 9 },
      { id: 'also-offer', visible: true, order: 10 },
      { id: 'app-download', visible: true, order: 11 },
      { id: 'chat', visible: true, order: 12 },
      { id: 'accept-cta', visible: true, order: 13 },
      { id: 'footer', visible: true, order: 14 },
    ],
  },
};

/**
 * Get default config for a tier
 */
export function getDefaultConfigForTier(tier = 'smb') {
  return DEFAULT_TIER_CONFIGS[tier] || DEFAULT_TIER_CONFIGS.smb;
}

/**
 * Get section metadata by ID
 */
export function getSectionMetadata(sectionId) {
  return SECTION_METADATA[sectionId];
}

/**
 * Get all section metadata
 */
export function getAllSectionMetadata() {
  return Object.values(SECTION_METADATA);
}

/**
 * Validate and normalize section config
 * Ensures all required fields are present and properly ordered
 */
export function normalizeSectionConfig(config) {
  if (!config || !Array.isArray(config.sections)) {
    return null;
  }

  const normalized = {
    tier: config.tier || 'smb',
    sections: config.sections
      .filter((section) => SECTION_METADATA[section.id]) // Remove invalid sections
      .map((section) => ({
        id: section.id,
        visible: section.visible !== false, // Default to true
        order: section.order || 0,
      }))
      .sort((a, b) => a.order - b.order), // Sort by order
  };

  return normalized;
}

/**
 * Get visible sections in order
 */
export function getVisibleSections(config) {
  if (!config || !Array.isArray(config.sections)) {
    return [];
  }

  return config.sections.filter((section) => section.visible).sort((a, b) => a.order - b.order);
}

/**
 * Check if a section should be visible given a config
 */
export function isSectionVisible(sectionId, config) {
  if (!config || !Array.isArray(config.sections)) {
    return false;
  }

  const section = config.sections.find((s) => s.id === sectionId);
  return section ? section.visible : false;
}

/**
 * Merge custom config with tier defaults
 * Custom config takes precedence over defaults
 */
export function mergeConfigWithDefaults(customConfig, tier = 'smb') {
  const defaultConfig = getDefaultConfigForTier(tier);

  if (!customConfig || !customConfig.sections) {
    return defaultConfig;
  }

  // Create a map of custom sections
  const customMap = {};
  customConfig.sections.forEach((section) => {
    customMap[section.id] = section;
  });

  // Merge: custom overrides default
  const merged = {
    tier: customConfig.tier || tier,
    sections: defaultConfig.sections.map((defaultSection) => {
      return customMap[defaultSection.id] || defaultSection;
    }),
  };

  return merged;
}

/**
 * Get section order for a specific config
 */
export function getSectionOrder(config) {
  if (!config || !Array.isArray(config.sections)) {
    return [];
  }

  return config.sections
    .filter((section) => section.visible)
    .sort((a, b) => a.order - b.order)
    .map((section) => section.id);
}

/**
 * Reorder sections
 */
export function reorderSections(config, newOrder) {
  if (!Array.isArray(newOrder)) {
    return config;
  }

  const updated = {
    ...config,
    sections: config.sections.map((section) => {
      const newOrderIndex = newOrder.indexOf(section.id);
      return {
        ...section,
        order: newOrderIndex >= 0 ? newOrderIndex + 1 : section.order,
      };
    }),
  };

  return updated;
}

/**
 * Toggle section visibility
 */
export function toggleSectionVisibility(config, sectionId, visible) {
  return {
    ...config,
    sections: config.sections.map((section) =>
      section.id === sectionId ? { ...section, visible } : section
    ),
  };
}

/**
 * Add a new section to config
 */
export function addSection(config, sectionId) {
  const metadata = getSectionMetadata(sectionId);
  if (!metadata) {
    return config;
  }

  const maxOrder = Math.max(...config.sections.map((s) => s.order), 0);

  return {
    ...config,
    sections: [
      ...config.sections,
      {
        id: sectionId,
        visible: true,
        order: maxOrder + 1,
      },
    ],
  };
}

/**
 * Remove a section from config (unless it's always-on)
 */
export function removeSection(config, sectionId) {
  const metadata = getSectionMetadata(sectionId);
  if (!metadata || metadata.alwaysOn) {
    return config;
  }

  return {
    ...config,
    sections: config.sections.filter((section) => section.id !== sectionId),
  };
}

const sectionsModule = {
  SECTION_METADATA,
  DEFAULT_TIER_CONFIGS,
  getDefaultConfigForTier,
  getSectionMetadata,
  getAllSectionMetadata,
  normalizeSectionConfig,
  getVisibleSections,
  isSectionVisible,
  mergeConfigWithDefaults,
  getSectionOrder,
  reorderSections,
  toggleSectionVisibility,
  addSection,
  removeSection,
};

export default sectionsModule;
